/* eslint-disable react-native/no-inline-styles */
import React, { useEffect, useRef, useState } from 'react';
import { Text, View, ViewStyle, TextInput, TextInputProps } from 'react-native';
import { useTranslation } from '@oolio-group/localization';
import theme from '../../common/theme';
import InputTextStyles from './InputText.styles';
import { useColors } from '../../hooks/useColors';

export interface InputTextProps extends TextInputProps {
  title?: string;
  testID: string;
  label?: string;
  value?: string | undefined;
  placeholder: string;
  errorMessage?: string;
  containerStyle?: ViewStyle | ViewStyle[];
  inputWrapperStyle?: ViewStyle | ViewStyle[];
  alignText?: 'left' | 'center' | 'right';
  numericOnly?: boolean;
}

const InputText: React.FC<InputTextProps> = ({
  title,
  value,
  label,
  testID,
  placeholder,
  errorMessage,
  containerStyle,
  inputWrapperStyle,
  alignText,
  numericOnly,
  ...props
}: InputTextProps) => {
  const inputRef = useRef<TextInput>(null);
  const { translate } = useTranslation();
  const styles = InputTextStyles();
  const [isFocused, setIsFocused] = useState<boolean | null>(false);
  const { colors } = useColors();

  useEffect(() => {
    const timeout = setTimeout(() => {
      const input = inputRef.current;
      if (input && !!props.editable && props.autoFocus === true) {
        input.focus();
      }
    }, 200);

    return () => {
      clearTimeout(timeout);
    };
  }, [inputRef, props.autoFocus, props.editable]);

  const onFocus = () => {
    setIsFocused(true);
  };
  const onBlur = () => {
    setIsFocused(false);
  };

  return (
    <View
      style={[
        containerStyle,
        {
          borderWidth: 2,
          borderColor: isFocused ? theme.colors.blueDark : colors.background,
          backgroundColor: colors.inputBackground,
        },
      ]}
    >
      {!!title && <Text style={styles.inputTitleText}>{title}</Text>}
      <View>
        <View
          style={[
            styles.inputWrapper,
            inputWrapperStyle,
            props.multiline && { height: 100 },
          ]}
        >
          {!!label && (
            <View style={styles.labelContainer}>
              <Text style={styles.labelText}>{label}</Text>
            </View>
          )}
          <TextInput
            testID={testID}
            value={value}
            placeholder={placeholder}
            placeholderTextColor={theme.colors.grey5}
            style={[
              styles.inputContainer,
              { textAlign: alignText },
              props.multiline && { paddingVertical: 12 },
            ]}
            autoCorrect={false}
            textAlign={alignText}
            {...props}
            onFocus={onFocus}
            onBlur={onBlur}
            ref={inputRef}
          />
        </View>
      </View>
      {numericOnly && value && !/^\d*\.?\d*$/.test(value) ? (
        <Text style={styles.errorText}>{translate('form.enterNumbers')}</Text>
      ) : null}
      {!!errorMessage ? (
        <Text style={styles.errorText}>{errorMessage}</Text>
      ) : null}
    </View>
  );
};

export default InputText;
